import { useState, useEffect, useCallback } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  Copy,
  ExternalLink,
  Camera,
  Info,
  Tag
} from 'lucide-react';
import { Button } from '@/components/ui';
import { BrandfolderAsset } from '../../types/brandfolder';
import { getResizedImageUrl } from '../../utils/imageUrl';

interface DamAssetDetailModalProps {
  asset: BrandfolderAsset;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Selected':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'Under Review':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const DamAssetDetailModal: React.FC<DamAssetDetailModalProps> = ({
  asset,
  onClose,
  onPrevious,
  onNext
}) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    setImageLoading(true);
    setImageError(false);
  }, [asset.bf_asset_id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (onPrevious) {
            event.preventDefault();
            onPrevious();
          }
          break;
        case 'ArrowRight':
          if (onNext) {
            event.preventDefault();
            onNext();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrevious, onNext]);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  const handleDownload = useCallback(() => {
    const link = document.createElement('a');
    link.href = asset.url;
    link.download = `brandfolder_${asset.bf_asset_id}`;
    link.referrerPolicy = 'no-referrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [asset]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Tag className="h-6 w-6 text-gray-600 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Brandfolder Asset
                  </h3>
                  <p className="text-sm text-gray-500 font-mono">
                    {asset.bf_asset_id}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {onPrevious && (
                  <Button variant="secondary" size="sm" onClick={onPrevious} className="p-2">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                )}
                {onNext && (
                  <Button variant="secondary" size="sm" onClick={onNext} className="p-2">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="secondary" size="sm" onClick={onClose} className="p-2">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 pb-4 sm:px-6 sm:pb-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Image — takes 3/4 of the width */}
              <div className="lg:col-span-3">
                <div className="relative bg-gray-100 rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
                  {!imageError ? (
                    <>
                      {imageLoading && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Camera className="h-12 w-12 text-gray-400 animate-pulse" />
                        </div>
                      )}
                      <img
                        src={getResizedImageUrl(asset.url, 1000)}
                        alt={`Brandfolder asset ${asset.bf_asset_id}`}
                        referrerPolicy="no-referrer"
                        className={`w-full h-full object-contain transition-opacity duration-200 ${
                          imageLoading ? 'opacity-0' : 'opacity-100'
                        }`}
                        onLoad={() => setImageLoading(false)}
                        onError={() => {
                          setImageError(true);
                          setImageLoading(false);
                        }}
                      />
                    </>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <Camera className="h-12 w-12 text-gray-400 mb-2" />
                      <p className="text-gray-500 text-sm">Failed to load image</p>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleDownload}
                    disabled={imageError}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>

                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => copyToClipboard(asset.url)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    {copySuccess ? 'Copied!' : 'Copy URL'}
                  </Button>

                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => window.open(asset.url, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Original
                  </Button>
                </div>
              </div>

              {/* Metadata sidebar */}
              <div className="space-y-6">
                {/* Status */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                    <Info className="h-4 w-4 mr-2" />
                    Status
                  </h4>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(asset.status)}`}>
                    {asset.status}
                  </span>
                </div>

                {/* Asset Details */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <Tag className="h-4 w-4 mr-2" />
                    Asset Details
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-500 block">Asset ID</span>
                      <span className="text-sm font-mono text-gray-900 break-all">{asset.bf_asset_id}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500 block">Source</span>
                      <span className="text-sm font-medium text-gray-900">Brandfolder</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
